/**
 * CommentHighlightPopover — View-only popover for comment highlights.
 *
 * Shows comment body on hover (wide screens) or tap (narrow screens).
 * Supports multiple overlapping comments — shows all when highlights overlap.
 * Dismisses on click outside or Escape.
 *
 * See: docs/features/document-system/document-comments.md (Phase 5/7)
 */
import React, { useEffect, useRef } from "react";

const CommentHighlightPopover = ({ comment, comments, coords, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Support both single comment and array of overlapping comments
  const items = comments || (comment ? [comment] : []);
  if (!items.length || !coords) return null;

  const style = {
    position: "fixed",
    top: coords.bottom + 6,
    left: Math.min(coords.left, window.innerWidth - 220),
    zIndex: 300,
  };

  return (
    <div className="comment-highlight-popover" style={style} ref={ref}>
      {items.map((c, i) => (
        <div key={c.id || i} className={i > 0 ? "comment-highlight-popover-divider" : undefined}>
          <div className="comment-highlight-popover-author">{c.authorName}</div>
          <div className="comment-highlight-popover-body">{c.body}</div>
        </div>
      ))}
    </div>
  );
};

export default React.memo(CommentHighlightPopover);
